import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import * as fromAuth from './auth.actions';
import { AuthConnection } from './auth-connection.model';
import { AuthToken } from 'src/app/modules/core/store/models/auth-token.model';
import { UserContext } from './user-context.model';
import { UserAuthKeyExpiration } from './auth-key-expired.model';
import { CultureCodeWithLabel, Language } from 'src/app/modules/core/languages/language.model';
export interface State {
  connection: AuthConnection;
  err: any;
  token: AuthToken;
  authExpiration: UserAuthKeyExpiration;
  mapLinkErr: any;
  dashboardLinkError: any;
  externalLinkError: any;
  languages?: string[];
  permissionsChangeTime?: string;
  ui: {
    isWorking: boolean;
    showPassword: boolean;
    isCompletingLogin: boolean;
    username: string;
    sharedMapIsWorking: boolean;
    sharedDashboardIsWorking: boolean;
    externalLinkIsWorking: boolean;
    showAcceptancePage: boolean;
    showBusinessUnitsWarning: boolean;
    businessUnitsWarningAccepted: boolean;
  };
}

export const initialState: State = {
  connection: null,
  err: null,
  token: null,
  authExpiration: null,
  mapLinkErr: null,
  dashboardLinkError: null,
  externalLinkError: null,
  languages: null,
  permissionsChangeTime: null,
  ui: {
    isWorking: false,
    showPassword: false,
    isCompletingLogin: false,
    username: null,
    sharedMapIsWorking: false,
    sharedDashboardIsWorking: false,
    externalLinkIsWorking: false,
    showAcceptancePage: false,
    showBusinessUnitsWarning: false,
    businessUnitsWarningAccepted: false
  }
};

export const reducer: ActionReducer<State, Action> = createReducer(
  initialState,
  on(fromAuth.loginStepEmailSuccess, (state, action) => {
    return {
      ...state,
      err: null,
      ui: {
        ...state.ui,
        isWorking: false
      }
    };
  }),
  on(fromAuth.showPassword, (state, action) => {
    return {
      ...state,
      connection: action.connection,
      err: null,
      ui: {
        ...state.ui,
        isWorking: false,
        showPassword: true,
        isCompletingLogin: false,
        username: action.username
      }
    };
  }),
  on(fromAuth.getConnectionFailed, (state, action) => {
    return {
      ...state,
      connection: null,
      err: action.err,
      ui: {
        ...state.ui,
        isWorking: false,
        isCompletingLogin: false
      }
    };
  }),
  on(fromAuth.setError, (state, action) => {
    return {
      ...state,
      connection: null,
      err: action.err,
      ui: {
        ...state.ui,
        isWorking: false,
        isCompletingLogin: false
      }
    };
  }),
  on(fromAuth.processAuth0LoginResultSuccess, (state, action) => {
    return {
      ...state,
      connection: null,
      err: null,
      ui: {
        ...state.ui,
        isCompletingLogin: false,
        isWorking: false
      }
    };
  }),
  on(fromAuth.setCompletingLogin, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isCompletingLogin: action.isCompleting
      }
    };
  }),
  on(fromAuth.loginUserNamePasswordSuccess, (state) => {
    return {
      ...state,
      connection: null,
      err: null,
      ui: {
        ...state.ui,
        isWorking: false,
        isCompletingLogin: false
      }
    };
  }),
  on(fromAuth.processHorizonLoginResultSuccess, (state) => {
    return {
      ...state,
      connection: null,
      err: null,
      ui: {
        ...state.ui,
        isWorking: false,
        isCompletingLogin: false
      }
    };
  }),
  on(fromAuth.processHorizonRedirectSuccess, (state, action) => {
    return {
      ...state,
      connection: null,
      err: null,
      authExpiration: null,
      ui: {
        ...state.ui,
        isWorking: false,
        isCompletingLogin: false
      }
    };
  }),
  on(fromAuth.showAcceptancePage, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showAcceptancePage: true
      }
    };
  }),
  on(fromAuth.hideAcceptancePage, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showAcceptancePage: false
      }
    };
  }),
  on(fromAuth.showBusinessUnitsWarning, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showBusinessUnitsWarning: true
      }
    };
  }),
  on(fromAuth.hideBusinessUnitsWarning, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showBusinessUnitsWarning: false
      }
    };
  }),
  on(fromAuth.setBusinessUnitsWarningAccepted, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        businessUnitsWarningAccepted: true
      }
    };
  }),
  on(fromAuth.backToEmail, (state, action) => {
    return {
      ...state,
      connection: null,
      err: null,
      authExpiration: null,
      ui: {
        showPassword: false,
        isCompletingLogin: false,
        username: null,
        isWorking: false,
        sharedMapIsWorking: false,
        sharedDashboardIsWorking: false,
        externalLinkIsWorking: false,
        showAcceptancePage: false,
        showBusinessUnitsWarning: false,
        businessUnitsWarningAccepted: false
      }
    };
  }),
  on(fromAuth.resetLogin, (state, action) => {
    return {
      connection: null,
      err: null,
      token: null,
      userContext: null,
      authExpiration: null,
      mapLinkErr: null,
      dashboardLinkError: null,
      externalLinkError: null,
      ui: {
        showPassword: false,
        isCompletingLogin: false,
        username: null,
        isWorking: false,
        sharedMapIsWorking: false,
        sharedDashboardIsWorking: false,
        externalLinkIsWorking: false,
        showAcceptancePage: false,
        showBusinessUnitsWarning: false,
        businessUnitsWarningAccepted: false
      }
    };
  }),
  on(fromAuth.logoutSuccess, (state, action) => {
    return {
      connection: null,
      err: null,
      token: null,
      userContext: null,
      authExpiration: null,
      mapLinkErr: null,
      dashboardLinkError: null,
      externalLinkError: null,
      ui: {
        showPassword: false,
        isCompletingLogin: false,
        username: null,
        isWorking: false,
        sharedMapIsWorking: false,
        sharedDashboardIsWorking: false,
        externalLinkIsWorking: false,
        showAcceptancePage: false,
        showBusinessUnitsWarning: false,
        businessUnitsWarningAccepted: false
      }
    };
  }),
  on(fromAuth.setToken, (state, { token }) => ({ ...state, token })),
  on(fromAuth.setIsWorking, (state, action) => {
    return {
      ...state,
      err: null,
      ui: {
        ...state.ui,
        isWorking: action.isWorking
      }
    };
  }),
  on(fromAuth.permissionsChanged, (state, action) => {
    return {
      ...state,
      permissionsChangeTime: Date.now().toString()
    };
  }),
  on(fromAuth.setExpired, (state, action) => {
    return {
      ...state,
      err: null,
      authExpiration: action.expirationStatus,
      ui: {
        ...state.ui
      }
    };
  }),
  on(fromAuth.loadAuthTokenByOrganization, (state, action) => {
    return {
      ...state,
      token: action.token
    };
  }),
  on(fromAuth.sharedMapAuthWorking, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        sharedMapIsWorking: action.isWorking
      }
    };
  }),
  on(fromAuth.setMapLinkError, (state, action) => {
    return {
      ...state,
      mapLinkErr: action.err
    };
  }),
  on(fromAuth.sharedDashboardAuthWorking, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        sharedDashboardIsWorking: action.isWorking
      }
    };
  }),
  on(fromAuth.setDashboardLinkError, (state, action) => {
    return {
      ...state,
      dashboardLinkError: action.err
    };
  }),
  on(fromAuth.externalLinkAuthWorking, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        externalLinkIsWorking: action.isWorking
      }
    };
  }),
  on(fromAuth.setExternalLinkError, (state, action) => {
    return {
      ...state,
      externalLinkError: action.err
    };
  }),
  on(fromAuth.loadAvailableLanguagesComplete, (state, action) => {
    return {
      ...state,
      languages: action.languages
    };
  })
);

export const getToken = (state: State) => state?.token;

export const getCurrentConnection = (state: State) => state?.connection;

export const getError = (state: State) => state?.err?.message;

export const getIsWorking = (state: State) => state?.ui?.isWorking;

export const getShowPassword = (state: State) => state?.ui.showPassword;

export const getIsCompletingLogin = (state: State) => state?.ui.isCompletingLogin;

export const getUsername = (state: State) => state?.ui.username;

export const getUserContext = (state: State) => (state?.token?.access_token ? new UserContext(state?.token?.access_token) : null);

export const getIsExpired = (state: State) => state?.authExpiration;

export const getSharedMapIsWorking = (state: State) => state?.ui?.sharedMapIsWorking;

export const getMapLinkError = (state: State) => state?.mapLinkErr?.message;

export const getSharedDashboardIsWorking = (state: State) => state?.ui?.sharedDashboardIsWorking;

export const getDashboardLinkError = (state: State) => state?.dashboardLinkError?.message;

export const getLanguages = (state: State) => state?.languages;

export const getExternalLinkIsWorking = (state: State) => state?.ui?.externalLinkIsWorking;

export const getExternalLinkLinkError = (state: State) => state?.externalLinkError?.message;

export const getPermissionsChangeTime = (state: State) => state?.permissionsChangeTime;

export const getShowAcceptancePage = (state: State) => state?.ui?.showAcceptancePage;
export const getShowBusinessUnitsWarning = (state: State) => state?.ui?.showBusinessUnitsWarning;
export const getBusinessUnitsWarningAccepted = (state: State) => state?.ui?.businessUnitsWarningAccepted === true;
